import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Input, Button, ListGroup, ListGroupItem, Dropdown, DropdownToggle, DropdownItem, DropdownMenu } from "reactstrap";
import { useGetGroupsQuery } from "../../../../backoffice/apis/groupsAPI";
import { Group } from "../../../../backoffice/dtos/groups/group";
import { Loading } from "../../../views/Loading";
import DepartmentCustomersSettings from "./Customers/DepartmentCustomersSettings";
import DepartmentsProductGroupsSettings from "./ProductGroups/DepartmentsProductGroupsSettings";
import DepartmentEditModal from "./Department/DepartmentEditModal";

export function DepartmentsSettings(
    {
        shown
    }: {
        shown: Boolean
    }
) {
    const [customerMenu, setCustomerMenu] = useState<number>(0)
    const [searchText, setSearchText] = useState<string>();
    const [modalData, setModalData] = useState<{open: boolean, department: Group | undefined}>({open: false, department: undefined});
    const [otherModalData, setOtherModalData] = useState<{ type: 'none' | 'customers' | 'product_groups', group: Group | undefined }>({ type: 'none', group: undefined });
    const { data: departments } = useGetGroupsQuery(undefined, {skip: !shown, refetchOnMountOrArgChange: true});
    const { t } = useTranslation(['department_info','common', 'product_groups', 'measurement_units'])

    const filteredData = useMemo(() => {
        if (searchText === undefined) {
            return departments;
        }
        if (departments === undefined) {
            return departments;
        }
        return departments.filter(
            (it) => {
                return it.description.includes(searchText);
            }
        )
    }, [departments, searchText])

    const handleChange = useCallback(
        (
            {
                target: { value },
            }: React.ChangeEvent<HTMLInputElement>
        ) => {
            if (value.length === 0) {
                setSearchText(undefined);
            } else {
                setSearchText(value)
            }
        },
    [])

    const openModal = useCallback((department: Group | undefined) => {
        setModalData({ open: true, department })
    }, [])

    const closeModal = useCallback(() => {
        setModalData({ open: false, department: undefined })
    }, [])

    const closeCustomerModal = useCallback(() => {
        setOtherModalData({ type: 'none', group: undefined })
    }, [])

    return (
        <>
            <Container className="my-2">
                <Row>
                    <Col>
                        <Input value={searchText} onChange={handleChange} placeholder={t('search', { ns: 'common' })} />
                    </Col>
                    <Col sm="auto">
                        <Button color='primary' onClick={() => openModal(undefined)}>
                            <span className='fa fa-lg fa-plus me-2' />{t('Add', { ns: 'common' })}
                        </Button>
                    </Col>
                </Row>
                <ListGroup className='mt-4'>
                    <ListGroupItem >
                        <Row>
                            <Col lg={2}><h5> {t('Name', { ns: 'common' })} </h5></Col>
                            <Col lg={2}></Col>
                        </Row>
                    </ListGroupItem>
                    {filteredData
                        ? filteredData.map(
                            (group, index) => (
                                <ListGroupItem key={index} >
                                    <Row  divider>
                                        <Col lg={2} className='d-flex flex-column justify-content-center'>{group.description}</Col>
                                        <Col className='d-flex justify-content-end'>
                                            <Button color="primary " className="mx-1" onClick={() => openModal(group)} >
                                                <i className='fa fa-edit fa-lg'></i>
                                            </Button>
                                            <Dropdown isOpen={customerMenu === group.id} toggle={() => setCustomerMenu(ref => ref === group.id ? 0 : group.id)}>
                                                <DropdownToggle color="primary">
                                                    ⋯
                                                </DropdownToggle>
                                                <DropdownMenu>
                                                    <DropdownItem onClick={() => setOtherModalData({type: 'customers', group})}>{t('Customers')}</DropdownItem>
                                                    <DropdownItem onClick={() => setOtherModalData({type: 'product_groups', group})}>{t('Product Groups')}</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </Col>
                                    </Row>
                                </ListGroupItem>
                            )
                        )
                        : <Loading className="m-2"/>
                    }
                </ListGroup>
            </Container>
            {
                modalData.open
                    && <DepartmentEditModal isOpen={modalData.open} department={modalData.department} toggle={closeModal}/>
            }
            {
                otherModalData?.type === 'customers'
                    && otherModalData.group !== undefined
                    && <DepartmentCustomersSettings group={otherModalData.group} toggle={closeCustomerModal}/>
            }
            {
                otherModalData?.type === 'product_groups'
                    && otherModalData.group !== undefined
                    && <DepartmentsProductGroupsSettings group={otherModalData.group} toggle={closeCustomerModal}/>
            }
        </>
    );
}
